import { useState, useRef, useEffect } from "react";
import { MEMORY_ITEMS, OBJECTS } from "@/data/exercises";
import { Check } from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function MemoryMatch({ onComplete }) {
  const [cards, setCards] = useState([]);
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const lock = useRef(false);
  const start = useRef(Date.now());

  useEffect(() => {
    const deck = shuffle([...MEMORY_ITEMS, ...MEMORY_ITEMS]).map((key, i) => ({
      uid: i,
      key,
    }));
    setCards(deck);
  }, []);

  useEffect(() => {
    if (cards.length && matched.length === cards.length) {
      const duration = Math.round((Date.now() - start.current) / 1000);
      const best = MEMORY_ITEMS.length;
      const accuracy = Math.max(20, Math.round((best / Math.max(moves, best)) * 100));
      const t = setTimeout(() => onComplete({ score: accuracy, accuracy, duration }), 700);
      return () => clearTimeout(t);
    }
  }, [matched, cards, moves, onComplete]);

  const handleFlip = (idx) => {
    if (lock.current) return;
    if (flipped.includes(idx) || matched.includes(idx)) return;
    const next = [...flipped, idx];
    setFlipped(next);
    if (next.length === 2) {
      setMoves((m) => m + 1);
      lock.current = true;
      const [a, b] = next;
      if (cards[a].key === cards[b].key) {
        setTimeout(() => {
          setMatched((m) => [...m, a, b]);
          setFlipped([]);
          lock.current = false;
        }, 500);
      } else {
        setTimeout(() => {
          setFlipped([]);
          lock.current = false;
        }, 900);
      }
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto" data-testid="memory-game">
      <p className="text-center text-2xl font-patient font-bold text-gray-700 mb-8">
        Retrouvez les deux images identiques
      </p>
      <div className="grid grid-cols-3 gap-6">
        {cards.map((card, idx) => {
          const isUp = flipped.includes(idx) || matched.includes(idx);
          const isMatched = matched.includes(idx);
          return (
            <button
              key={card.uid}
              onClick={() => handleFlip(idx)}
              data-testid={`memory-card-${idx}`}
              className={`aspect-square rounded-3xl border-4 flex items-center justify-center overflow-hidden transition-transform duration-200 active:scale-95 ${
                isMatched
                  ? "border-emerald-600 bg-emerald-50"
                  : isUp
                  ? "border-amber-300 bg-white"
                  : "border-emerald-700/20 bg-[#F8E4C7]"
              }`}
            >
              {isUp ? (
                <div className="relative w-full h-full">
                  <img
                    src={OBJECTS[card.key].img}
                    alt={OBJECTS[card.key].label}
                    className="w-full h-full object-cover"
                  />
                  {isMatched && (
                    <div className="absolute inset-0 bg-emerald-600/30 flex items-center justify-center">
                      <Check className="w-12 h-12 text-white" strokeWidth={3} />
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-5xl font-patient font-black text-emerald-800/40">?</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
